import { motion } from "framer-motion";
import styles from "./ModalRoot.module.css";
import { useModal } from "../../hooks/useModalHook";
import { ReactComponent as CloseIcon } from "../../images/svg/close.svg";
import { ReactComponent as CloseIconBlack } from "../../images/svg/close_black.svg";
import { modalTransition, overlayTransition, burgerTransition } from "../../motionConfig";

import ModalVideo from "../ModalVideo/ModalVideo";
import ModalCheckup from "../ModalCheckup/ModalCheckup";
import ModalImage from "../ModalImage/ModalImage";
import ModalSuccess from "../ModalSuccess/ModalSuccess";
import ModalAppointment from "../ModalAppointment/ModalAppointment";
import ModalPDF from "../ModalPDF/ModalPDF";
import ModalSuccessQuestion from "../ModalSuccessQuestion/ModalSuccessQuestion";
import BurgerModal from "../BurgerModal/BurgerModal";
import { useMediaQuery } from "react-responsive";
import { useEffect, useMemo, useCallback, useRef, useState } from "react";

import checkups_img_01 from "../../images/checkups_01.webp"
import checkups_img_02 from "../../images/checkups_02.webp"
import checkups_img_03 from "../../images/checkups_03.webp"
import checkups_img_04 from "../../images/checkups_04.webp"

const checkups = {
  "items": [
    {
      "id": 1,
      "name": "Гинекологический чекап",
      "price": 15200,
      "img": checkups_img_01,
      "visits": [
        {
          "name": "Первый визит",
          "offset_days": 0,
          "services": [
            "Гинекологический мазок на флору.",
            "Цитология жидкостная PAP-тест (BD SurePath).",
            "Взятие материала для лабораторного исследования (мазки, соскобы, посевы).",
            "УЗИ гинекологическое экспертное.",
            "УЗИ молочных желез."
          ]
        },
        {
          "name": "Второй визит",
          "offset_days": 5,
          "services": [
            "Повторный приём гинеколога (возможно онлайн)"
          ]
        }
      ]
    },
    {
      "id": 2,
      "name": "Базовый мужской чекап",
      "price": 10100,
      "img": checkups_img_02,
      "visits": [
        {
          "name": "Состав",
          "offset_days": 0,
          "services": [
            "ПЦР: Chlamydia trachomatis, Mycoplasma genitalium, Mycoplasma hominis, Neisseria gonorrhoeae, Trichomonas vaginalis, Ureaplasma spp (Ureaplasma urealyticum-parvum).",
            "Приём врача уролога-андролога.",
            "Взятие материала для лабораторного исследования.",
            "УЗИ урологическое экспертное с допплерометрией.",
            "Предварительная оценка качества спермы с MAP-тестом."
          ]
        },
        {
          "name": "Через 3 дня",
          "offset_days": 0,
          "services": [
            "Приём уролога-андролога повторный."
          ]
        }
      ]
    },
    {
      "id": 3,
      "name": "Женский репродуктивный",
      "price": 20600,
      "img": checkups_img_03,
      "visits": [
        {
          "name": "Первый визит",
          "offset_days": 0,
          "services": [
            "ЛГ, АМГ, пролактин, ТТГ, Т4 свободный, ферритин, 25-ОН витамин D.",
            "Взятие материала для лабораторного исследования.",
            "Гинекологический мазок на флору.",
            "Исследование соскобов с шейки матки и цервикального канала (мазок на цитологию).",
            "Дифференцированное определение ДНК ВПЧ (Вирус папилломы человека, HPV) высокого онкогенного риска, 14 типов.",
            "УЗИ гинекологическое экспертное.",
            "УЗИ молочных желез.",
            "УЗИ щитовидной железы."
          ]
        },
        {
          "name": "Второй визит",
          "offset_days": 5,
          "services": [
            "Экспертный приём акушера-гинеколога по результатам проведённого обследования."
          ]
        }
      ]
    },
    {
      "id": 4,
      "name": "Мужской репродуктивный",
      "price": 27500,
      "img": checkups_img_04,
      "visits": [
        {
          "name": "Состав",
          "offset_days": 0,
          "services": [
            "ПЦР: Chlamydia trachomatis, Mycoplasma genitalium, Mycoplasma hominis, Neisseria gonorrhoeae, Trichomonas vaginalis, Ureaplasma spp (Ureaplasma urealyticum-parvum).",
            "Гормональные исследования: ГСПГ (глобулин, связывающий половые гормоны), ЛГ (лютеинизирующий гормон), ФСГ (фолликулостимулирующий гормон), ТТГ (тиреотропный гормон), тестостерон общий.",
            "УЗИ урологическое экспертное с допплерометрией",
            "Спермограмма",
            "Исследование фрагментации ДНК в сперматозоидах методом TUNEL (без градиента)",
            "Предварительная оценка качества спермы с МАР-тестом",
            "Приём врача уролога-андролога"
          ]
        }
      ]
    }
  ]
}


const ModalRoot = () => {
  const { modal, closeModal } = useModal();

  // Реф для кнопки закрытия
  const closeButtonRef = useRef(null);
  

  // Мемоизируем проверку типа модалки
  const isBurgerModal = useMemo(() => modal.type === "burger", [modal.type]);


  // Обработка клавиши Escape и управление фокусом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && modal.open) {
        closeModal();
      }
    };

    if (modal.open) {
      document.addEventListener('keydown', handleEscape);
      
      // Устанавливаем фокус на кнопку закрытия при открытии
      let timer;
      if (closeButtonRef.current && !isBurgerModal) {
        timer = setTimeout(() => {
          closeButtonRef.current?.focus();
        }, 100);
      }
      
      // Захватываем фокус для модального окна
      const focusableElements = document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
              lastFocusable?.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusable) {
              firstFocusable?.focus();
              e.preventDefault();
            }
          }
        }
      };
      
      document.addEventListener('keydown', handleTabKey);
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTabKey);
        if (timer) clearTimeout(timer);
      };
    }
  }, [modal.open, closeModal, isBurgerModal]);

  let Content;
  try {
    switch (modal.type) {
      case "video":
        Content = <ModalVideo src={modal.content} />;
        break;
      case "checkup":
        Content = <ModalCheckup src={modal.content} />;
        break;
      case "image":
        if (!modal.content) {
          Content = <div className={styles.unknownModalType}>Изображение не найдено</div>;
        } else {
          Content = <ModalImage src={modal.content} title={modal.props?.title} />;
        }
        break;
      case "appointment":
        Content = <ModalAppointment doctor={modal.props?.doctor} src={modal.content} />;
        break;
      case "success":
        Content = <ModalSuccess src={modal.content} />;
        break;
      case "pamyatka":
        if (!modal.content) {
          Content = <div className={styles.unknownModalType}>PDF файл не найден</div>;
        } else {
          Content = <ModalPDF src={modal.content} title={modal.props?.title} />;
        }
        break;
      case "success_question":
        Content = <ModalSuccessQuestion src={modal.content} />;
        break;
      case "checkup_1":
      case "checkup_2":
      case "checkup_3":
      case "checkup_4": {
        const match = modal.type.match(/checkup_(\d+)/);
        const checkupId = match ? parseInt(match[1], 10) : 1;
        const checkup = checkups.items.find(item => item.id === checkupId);
        Content = checkup ? <ModalCheckup checkup={checkup} {...modal.props} /> : 
          <div className={styles.unknownModalType}>Чекап не найден</div>;
        break;
      }
      default:
        Content = <div className={styles.unknownModalType}>Неизвестный тип модалки: {modal.type}</div>;
    }
  } catch (error) {
    console.error('Ошибка рендеринга модального окна:', error);
    Content = <div className={styles.unknownModalType}>Ошибка загрузки модального окна</div>;
  }

  const handleOverlayClick = useCallback((e) => {
    console.log('Overlay click detected:', {
      target: e.target,
      currentTarget: e.currentTarget,
      targetTagName: e.target.tagName,
      targetClasses: e.target.classList.toString(),
      isOverlay: e.target === e.currentTarget
    });
    
    // Простая проверка - если клик по самому overlay
    if (e.target === e.currentTarget) {
      console.log('Overlay clicked, closing modal');
      closeModal();
    } else {
      console.log('Click was not on overlay, ignoring');
    }
  }, [closeModal]);

  // Мемоизируем проверку мобильного устройства
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  // Анимационные параметры для обычных модальных окон
  const animationProps = useMemo(() => ({
    initial: { scale: 0.8, opacity: 0, y: 50, x: 0 },
    animate: { scale: 1, opacity: 1, y: 0, x: 0 },
    exit: { scale: 0.8, opacity: 0, y: 50, x: 0 },
    transition: modalTransition
  }), []);

  // Дополнительная валидация
  if (modal.open && !modal.type) {
    console.warn('Modal is open but type is missing');
    return null;
  }

  return (
    <>
      {modal.open && modal.type && (
        <motion.div
          className={styles.overlay}
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={overlayTransition}
        >
          {isBurgerModal ? (
            <>
              {/* Overlay для бургер-меню */}
              <div
                onClick={closeModal}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100vh',
                  zIndex: 2001,
                  background: 'transparent',
                }}
              />
              {/* Бургер-меню справа */}
              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={burgerTransition}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  width: isMobile ? '100%' : '400px', // Фиксированная ширина
                  height: '100vh',
                  zIndex: 2002,
                }}
              >
                <BurgerModal />
              </motion.div>
            </>
          ) : (
            <motion.div
              className={styles.modal}
              initial={animationProps.initial}
              animate={animationProps.animate}
              exit={animationProps.exit}
              transition={animationProps.transition}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              aria-describedby="modal-content"
            >
              <div className={styles.modalWrapper}>
                <button
                  ref={closeButtonRef}
                  className={styles.closeModal}
                  onClick={closeModal}
                  aria-label="Закрыть"
                >
                  {isMobile ? <CloseIconBlack /> : <CloseIcon />}
                </button>
                <div
                  className={styles.modalContent}
                  id="modal-content"
                >
                  {Content}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </>
  );
};

export default ModalRoot;
